import { LoadingSpinner, PageHeaderWrapper, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, ThemeProvider } from "@mui/material";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useCallback, useMemo, useRef } from "react";
import { Redirect, useHistory } from "react-router-dom";

import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { useGetExternalAccounts } from "./api/useGetExternalAccounts";
import { useGetPayoutSettings } from "./api/useGetPayoutSettings";
import { useUpdatePayoutSettings } from "./api/useUpdatePayoutSettings";
import {
  type PayoutSettingsChangeableSettings,
  PayoutSettingsPageContent,
} from "./components/PayoutSettingsPageContent";
import { SetUpStripeAccountPageContent } from "./components/SetUpStripeAccountPageContent";
import { UnsavedChangesBottomSheet } from "./components/UnsavedChangesBottomSheet";
import { useGoToStripeExpressSignIn } from "./hooks/useGoToStripeExpressSignIn";
import { useGoToStripeOnboarding } from "./hooks/useGoToStripeOnboarding";

export interface PayoutSettingsPageProps {
  onClickBack?: () => void;
}

export function PayoutSettingsPage(props: PayoutSettingsPageProps) {
  const { onClickBack } = props;

  const history = useHistory();
  const {
    data: payoutSettings,
    isLoading: isPayoutSettingsLoading,
    isError: isPayoutSettingsError,
  } = useGetPayoutSettings();
  const {
    data: externalAccountsResponse,
    isLoading: isExternalAccountsLoading,
    isError: isExternalAccountsError,
  } = useGetExternalAccounts();
  const { mutate: updatePayoutSettings, isLoading: isSaveLoading } = useUpdatePayoutSettings();
  const { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading } =
    useGoToStripeExpressSignIn();
  const { goToStripeOnboarding, isGoToStripeOnboardingLoading } = useGoToStripeOnboarding();
  const unsavedChangesBottomSheetModalState = useModalState();
  const draftSettings = useRef<PayoutSettingsChangeableSettings | undefined>(undefined);

  const externalAccounts = useMemo(
    () => externalAccountsResponse?.data.map((x) => x.attributes) ?? [],
    [externalAccountsResponse]
  );

  const isLoading = isPayoutSettingsLoading || isExternalAccountsLoading;
  const isError = isPayoutSettingsError || isExternalAccountsError;

  const onDraftSettingsUpdated = useCallback(
    (newDraftSettings: PayoutSettingsChangeableSettings | undefined) => {
      draftSettings.current = newDraftSettings;
    },
    []
  );

  const goBack = useCallback(() => {
    if (draftSettings.current) {
      unsavedChangesBottomSheetModalState.openModal();
    } else if (isDefined(onClickBack)) {
      onClickBack();
    } else {
      history.goBack();
    }
  }, [unsavedChangesBottomSheetModalState, history, onClickBack]);

  const onSave = useCallback(() => {
    if (draftSettings.current) {
      updatePayoutSettings({
        payoutSchedule: draftSettings.current.selectedPayoutSchedule,
        preferredExternalAccountStripeId:
          draftSettings.current.selectedPreferredExternalAccountStripeId,
      });
    }
  }, [updatePayoutSettings]);

  if (isError) {
    return <Redirect to="/" />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow onClickBack={goBack} />
        </PageHeaderWrapper>

        {isLoading && (
          <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
            <LoadingSpinner size="large" />
          </Box>
        )}
        {!isLoading && payoutSettings.meta.payoutCapability === "none" && (
          <SetUpStripeAccountPageContent
            isGoToStripeLoading={isGoToStripeOnboardingLoading}
            onGoToStripe={goToStripeOnboarding}
          />
        )}
        {!isLoading && payoutSettings.meta.payoutCapability !== "none" && (
          <PayoutSettingsPageContent
            payoutSchedule={payoutSettings.attributes.payoutSchedule}
            preferredExternalAccountStripeId={
              payoutSettings.attributes.preferredExternalAccountStripeId
            }
            instantPayFee={payoutSettings.meta.instantPayoutFee}
            payoutCapability={payoutSettings.meta.payoutCapability}
            externalAccounts={externalAccounts}
            isSaveLoading={isSaveLoading}
            isGoToStripeLoading={isGoToStripeExpressSignInLoading}
            onDraftSettingsUpdated={onDraftSettingsUpdated}
            onSave={onSave}
            onGoToStripe={goToStripeExpressSignIn}
          />
        )}
      </PageWrapper>
      <UnsavedChangesBottomSheet
        modalState={unsavedChangesBottomSheetModalState}
        onSave={() => {
          unsavedChangesBottomSheetModalState.closeModal();
          onSave();
        }}
        onDiscard={() => {
          draftSettings.current = undefined;
          goBack();
        }}
      />
    </ThemeProvider>
  );
}
